const APPVIEW_AUDIENCE = 'did:web:api.bsky.app#bsky_appview';
const CHAT_AUDIENCE = 'did:web:api.bsky.chat#bsky_chat';

export const BLUESKY_APPVIEW_METHODS = Object.freeze([
  'app.bsky.actor.getProfile',
  'app.bsky.actor.searchActors',
  'app.bsky.feed.getAuthorFeed',
  'app.bsky.feed.getLikes',
  'app.bsky.feed.getPosts',
  'app.bsky.feed.getTimeline',
  'app.bsky.feed.searchPosts',
  'app.bsky.graph.getFollowers',
  'app.bsky.graph.getFollows',
  'app.bsky.graph.getKnownFollowers',
  'app.bsky.notification.getUnreadCount',
  'app.bsky.notification.listNotifications',
  'app.bsky.notification.updateSeen',
]);

export const BLUESKY_CHAT_METHODS = Object.freeze([
  'chat.bsky.convo.getConvoForMembers',
  'chat.bsky.convo.getMessages',
  'chat.bsky.convo.listConvos',
  'chat.bsky.convo.sendMessage',
  'chat.bsky.convo.updateRead',
]);

const REPO_PERMISSIONS = Object.freeze([
  {
    collections: ['app.bsky.actor.profile'],
    actions: ['create', 'update'],
  },
  {
    collections: [
      'app.bsky.feed.like',
      'app.bsky.feed.repost',
      'app.bsky.graph.follow',
      'me.skyspace.chat.action',
      'me.skyspace.chat.message',
      'me.skyspace.graffiti.action',
      'me.skyspace.graffiti.mark',
      'me.skyspace.profile.comment',
    ],
    actions: ['create', 'delete'],
  },
  {
    collections: [
      'app.bsky.feed.post',
      'me.skyspace.aesthetics',
      'me.skyspace.content',
    ],
    actions: ['create', 'update', 'delete'],
  },
  {
    collections: [
      'me.skyspace.chat.room',
      'me.skyspace.graffiti.wall',
    ],
    actions: ['create', 'update'],
  },
  {
    collections: ['app.bsky.graph.block'],
    actions: ['create'],
  },
]);

const IMAGE_TYPES = Object.freeze([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

function parameter(value) {
  return encodeURIComponent(value)
    .replace(/%2F/gi, '/')
    .replace(/%3A/gi, ':');
}

function permission(resource, values) {
  const params = [];
  for (const [key, entries] of Object.entries(values)) {
    for (const value of entries) params.push(`${key}=${parameter(value)}`);
  }
  return `${resource}?${params.join('&')}`;
}

function positionalPermission(resource, positional, values = {}) {
  const suffix = permission('', values).slice(1);
  return `${resource}:${parameter(positional)}${suffix ? `?${suffix}` : ''}`;
}

function repoPermissions({ collections, actions }) {
  return collections.map(collection => (
    positionalPermission('repo', collection, { action: actions })
  ));
}

function rpcPermissions(methods, audience) {
  return methods.map(method => (
    positionalPermission('rpc', method, { aud: [audience] })
  ));
}

export function skyspacePrivateServiceAudience(hostname) {
  return `${skyspacePrivateServiceDid(hostname)}#skyspace_private`;
}

export function skyspacePrivateServiceDid(hostname) {
  return `did:web:${String(hostname).trim().toLowerCase()}`;
}

export function skyspaceOAuthScope(hostname) {
  return [
    'atproto',
    ...REPO_PERMISSIONS.flatMap(repoPermissions),
    permission('blob', { accept: IMAGE_TYPES }),
    ...rpcPermissions(BLUESKY_APPVIEW_METHODS, APPVIEW_AUDIENCE),
    ...rpcPermissions(BLUESKY_CHAT_METHODS, CHAT_AUDIENCE),
    ...rpcPermissions(
      ['me.skyspace.auth.createSession'],
      skyspacePrivateServiceDid(hostname),
    ),
    ...rpcPermissions(
      ['me.skyspace.auth.createSession'],
      skyspacePrivateServiceAudience(hostname),
    ),
  ].join(' ');
}

export function isGranularSkyspaceSession(session, hostname = '') {
  const scope = String(session?.token?.scope || '');
  const tokens = new Set(scope.split(/\s+/).filter(Boolean));
  const privateSessionPermission = hostname
    ? rpcPermissions(
      ['me.skyspace.auth.createSession'],
      skyspacePrivateServiceDid(hostname),
    )[0]
    : '';
  const legacyPrivateSessionPermission = hostname
    ? rpcPermissions(
      ['me.skyspace.auth.createSession'],
      skyspacePrivateServiceAudience(hostname),
    )[0]
    : '';
  return tokens.has('atproto')
    && !tokens.has('transition:generic')
    && !tokens.has('transition:chat.bsky')
    && ![...tokens].some(token => (
      token.startsWith('rpc?lxm=')
      || token.startsWith('repo?collection=')
    ))
    && [...tokens].some(token => (
      token.startsWith('repo')
      || token.startsWith('rpc')
      || token.startsWith('blob')
    ))
    && (!hostname
      || tokens.has(privateSessionPermission)
      || tokens.has(legacyPrivateSessionPermission));
}
